import { ChangeDetectionStrategy, Component, input, model } from '@angular/core';
import { MatTabsModule } from '@angular/material/tabs';

/** One tab. `id` is what `selectedId` reports. */
export interface BzTab {
  id: string;
  label: string;
  disabled?: boolean;
}

/**
 * A tab bar, built on `mat-tab-group`.
 *
 * Material rather than hand-built for the keyboard contract: roving tabindex,
 * arrow keys, `Home`/`End`, and the `role="tab"` / `aria-selected` /
 * `aria-controls` wiring between the tab and its panel.
 *
 * This renders the tab strip only and reports which tab is active. Panel
 * content stays with the caller, because tab bodies are almost always
 * route-driven or lazily loaded in this application — projecting them through
 * here would force every panel to be constructed whether or not it is shown.
 */
@Component({
  selector: 'bz-tabs',
  imports: [MatTabsModule],
  templateUrl: './bz-tabs.html',
  styleUrl: './bz-tabs.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BzTabs {
  readonly tabs = input<readonly BzTab[]>([]);

  /** Id of the active tab. Two-way, so a caller can drive it from a route. */
  readonly selectedId = model<string | null>(null);

  readonly ariaLabel = input('');

  protected onIndexChange(index: number): void {
    const tab = this.tabs()[index];
    if (tab) this.selectedId.set(tab.id);
  }

  protected get selectedIndex(): number {
    const index = this.tabs().findIndex((tab) => tab.id === this.selectedId());
    return index === -1 ? 0 : index;
  }
}
